<template>
  <div class="space-y-6">
    <div>
      <h2 class="text-xl font-semibold mb-2">Uppladdningshistorik</h2>
      <p class="text-gray-600">Översikt av alla uppladdade filer</p>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <BaseIcon icon="file-excel" class="text-blue-600 text-lg" />
          </div>
          <div>
            <p class="text-sm text-gray-600">Pipeline-filer</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(uploads.length) }}</p>
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #FEF3C7">
            <BaseIcon icon="trophy" class="text-lg" style="color: #F1A239" />
          </div>
          <div>
            <p class="text-sm text-gray-600">Vunna affärer-filer</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(wonDealsUploads.length) }}</p>
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <BaseIcon icon="database" class="text-green-600 text-lg" />
          </div>
          <div>
            <p class="text-sm text-gray-600">Totalt antal rader</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(totalRows) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- File List -->
    <div class="bg-white rounded-lg shadow p-6">
      <div v-if="allUploads.length === 0" class="text-center text-gray-500 py-8">
        <BaseIcon icon="file-excel" class="text-4xl mb-4 opacity-50" />
        <p>Inga filer har laddats upp ännu</p>
      </div>
      <div v-else class="overflow-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b">
              <th class="text-left p-3">#</th>
              <th class="text-left p-3">Typ</th>
              <th class="text-left p-3">Filnamn</th>
              <th class="text-left p-3">Uppladdningsdatum</th>
              <th class="text-right p-3">Antal rader</th>
              <th class="text-right p-3">Ordervärde</th>
              <th class="text-right p-3">Antal säljare</th>
              <th class="text-right p-3">Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(upload, index) in sortedAllUploads" :key="upload._key" class="border-b hover:bg-gray-50">
              <td class="p-3 text-gray-600">{{ index + 1 }}</td>
              <td class="p-3">
                <span v-if="upload._type === 'pipeline'" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white" style="background-color: #3273DC">
                  Pipeline
                </span>
                <span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white" style="background-color: #F1A239">
                  Vunna affärer
                </span>
              </td>
              <td class="p-3">
                <div class="flex items-center gap-2">
                  <BaseIcon :icon="upload._type === 'pipeline' ? 'file-excel' : 'trophy'" :class="upload._type === 'pipeline' ? 'text-blue-600' : ''" :style="upload._type === 'wondeals' ? 'color: #F1A239' : ''" />
                  <span class="font-medium">{{ upload.fileName }}</span>
                </div>
              </td>
              <td class="p-3">{{ new Date(upload.uploadDate).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}</td>
              <td class="text-right p-3">{{ formatNumber(upload.rowCount) }}</td>
              <td class="text-right p-3">{{ formatNumber(getOrderValue(upload)) }} kr</td>
              <td class="text-right p-3">{{ formatNumber(getUniqueSellers(upload)) }}</td>
              <td class="text-right p-3">
                <button class="px-2 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50" @click="handleDelete(upload)">
                  <BaseIcon icon="trash" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PipelineFileRegister",
  props: {
    uploads: { type: Array, required: true },
    wonDealsUploads: { type: Array, default: () => [] },
  },
  computed: {
    allUploads() {
      const pipeline = this.uploads.map(u => ({ ...u, _type: 'pipeline', _key: 'p_' + u.id }));
      const wondeals = this.wonDealsUploads.map(u => ({ ...u, _type: 'wondeals', _key: 'w_' + u.id }));
      return [...pipeline, ...wondeals];
    },
    sortedAllUploads() {
      return [...this.allUploads].sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());
    },
    totalRows() {
      return this.allUploads.reduce((sum, u) => sum + u.rowCount, 0);
    },
  },
  methods: {
    formatNumber(num) {
      return num.toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    getOrderValue(upload) {
      return upload.data.reduce((sum, row) => sum + (Number(row["Ordervärde"]) || 0), 0);
    },
    getUniqueSellers(upload) {
      return new Set(upload.data.map((row) => row["Säljare"]).filter(Boolean)).size;
    },
    handleDelete(upload) {
      if (upload._type === 'pipeline') {
        this.$emit('delete-upload', upload.id);
      } else {
        this.$emit('delete-wondeals', upload.id);
      }
    },
  },
};
</script>
